import axiosNew from '../../helpers/axios'
import { uploadAttachmentToAWS } from '../taskRefactored/tasks.service'

const BASE = 'support/prototype'

const pickMessage = (payload, fallback) =>
    payload?.message ?? payload?.error ?? fallback

const fromResponse = (response, fallbackMessage) => {
    const payload = response?.data ?? {}
    return {
        success: Boolean(payload.success),
        message: pickMessage(payload, fallbackMessage),
        code: payload.code,
        result: payload.result ?? null,
        data: payload.data ?? null,
        totalCount: payload.totalCount,
        currentCount: payload.currentCount,
        isNext: payload.isNext,
        versionId: payload.versionId,
        version: payload.version,
        openCount: payload.openCount,
        orphanCount: payload.orphanCount,
        screens: payload.screens,
        currentVersionId: payload.currentVersionId,
        carriedComments: payload.carriedComments,
        raw: payload,
    }
}

const fromError = (error, fallbackMessage) => {
    const payload = error?.response?.data ?? {}
    return {
        success: false,
        message: pickMessage(payload, error?.message || fallbackMessage),
        code: payload.code,
        result: null,
        data: null,
        raw: payload,
    }
}

/** Step 1 — presigned S3 PUT for an HTML prototype file. */
export const getPrototypeUploadUrl = async ({ filename, contentType }) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/upload-url`,
            method: 'POST',
            data: { filename, contentType },
        })
        return fromResponse(response, 'Failed to get upload URL')
    } catch (error) {
        return fromError(error, 'Failed to get upload URL')
    }
}

/** Upload HTML file to S3 via presigned URL, then return publicLink. */
export const uploadPrototypeHtmlFile = async (file) => {
    if (!file) {
        return {
            success: false,
            message: 'No file selected',
            publicLink: null,
            fileName: null,
            fileSize: null,
        }
    }

    const filename = file.name || 'prototype.html'
    const contentType = file.type || 'text/html'

    const urlResult = await getPrototypeUploadUrl({ filename, contentType })
    const upload = urlResult.data
    if (!urlResult.success || !upload?.uploadUrl || !upload?.publicLink) {
        return {
            success: false,
            message: urlResult.message || 'Failed to get upload URL',
            code: urlResult.code,
            publicLink: null,
            fileName: filename,
            fileSize: file.size,
        }
    }

    const putResult = await uploadAttachmentToAWS({
        url: upload.uploadUrl,
        data: file,
    })
    const putResponse = putResult?.data
    const putOk =
        putResponse?.ok === true ||
        (typeof putResponse?.status === 'number' &&
            putResponse.status >= 200 &&
            putResponse.status < 300)

    if (!putOk) {
        return {
            success: false,
            message: 'Failed to upload file to storage',
            publicLink: null,
            fileName: filename,
            fileSize: file.size,
        }
    }

    return {
        success: true,
        message: urlResult.message || 'Upload complete',
        publicLink: upload.publicLink,
        fileName: upload.filename || filename,
        fileSize: file.size,
    }
}

export const listPrototypes = async ({
    projectId,
    search = '',
    skip = 0,
    limit = 25,
} = {}) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/list`,
            method: 'POST',
            data: {
                projectId,
                ...(search ? { search } : {}),
                skip,
                limit,
            },
        })
        const parsed = fromResponse(response, 'Failed to load prototypes')
        return {
            ...parsed,
            result: Array.isArray(parsed.result) ? parsed.result : [],
        }
    } catch (error) {
        return {
            ...fromError(error, 'Failed to load prototypes'),
            result: [],
            totalCount: 0,
            isNext: false,
        }
    }
}

export const getPrototypeDetail = async (id, { versionId } = {}) => {
    try {
        const params = {}
        if (versionId) params.versionId = versionId
        const response = await axiosNew({
            url: `${BASE}/${id}`,
            method: 'GET',
            params,
        })
        return fromResponse(response, 'Failed to load prototype')
    } catch (error) {
        return fromError(error, 'Failed to load prototype')
    }
}

export const createPrototype = async ({
    projectId,
    name,
    description,
    fileUrl,
    fileName,
    fileSize,
    designWidth,
} = {}) => {
    try {
        const response = await axiosNew({
            url: BASE,
            method: 'POST',
            data: {
                projectId,
                name,
                ...(description != null ? { description } : {}),
                fileUrl,
                ...(fileName != null ? { fileName } : {}),
                ...(fileSize != null ? { fileSize } : {}),
                ...(designWidth != null ? { designWidth } : {}),
            },
        })
        return fromResponse(response, 'Failed to create prototype')
    } catch (error) {
        return fromError(error, 'Failed to create prototype')
    }
}

export const updatePrototype = async (
    id,
    { name, description, designWidth } = {}
) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/${id}`,
            method: 'PUT',
            data: {
                ...(name != null ? { name } : {}),
                ...(description != null ? { description } : {}),
                ...(designWidth != null ? { designWidth } : {}),
            },
        })
        return fromResponse(response, 'Failed to update prototype')
    } catch (error) {
        return fromError(error, 'Failed to update prototype')
    }
}

export const deletePrototype = async (id) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/${id}`,
            method: 'DELETE',
        })
        return fromResponse(response, 'Failed to delete prototype')
    } catch (error) {
        return fromError(error, 'Failed to delete prototype')
    }
}

export const listPrototypeVersions = async (id) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/${id}/versions`,
            method: 'GET',
        })
        const parsed = fromResponse(response, 'Failed to load versions')
        return {
            ...parsed,
            result: Array.isArray(parsed.result) ? parsed.result : [],
        }
    } catch (error) {
        return {
            ...fromError(error, 'Failed to load versions'),
            result: [],
        }
    }
}

export const uploadPrototypeVersion = async (
    id,
    { fileUrl, fileName, fileSize, changeNote, carryComments = true } = {}
) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/${id}/version`,
            method: 'POST',
            data: {
                fileUrl,
                ...(fileName != null ? { fileName } : {}),
                ...(fileSize != null ? { fileSize } : {}),
                ...(changeNote != null ? { changeNote } : {}),
                carryComments,
            },
        })
        return fromResponse(response, 'Failed to upload version')
    } catch (error) {
        return fromError(error, 'Failed to upload version')
    }
}

export const activatePrototypeVersion = async (id, versionId) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/${id}/version/${versionId}/activate`,
            method: 'PUT',
        })
        return fromResponse(response, 'Failed to activate version')
    } catch (error) {
        return fromError(error, 'Failed to activate version')
    }
}

export const listPrototypeComments = async (
    id,
    { versionId, resolved, screenHash, isOrphaned } = {}
) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/${id}/comments`,
            method: 'POST',
            data: {
                ...(versionId ? { versionId } : {}),
                ...(resolved != null ? { resolved } : {}),
                ...(screenHash != null ? { screenHash } : {}),
                ...(isOrphaned != null ? { isOrphaned } : {}),
            },
        })
        const parsed = fromResponse(response, 'Failed to load comments')
        return {
            ...parsed,
            result: Array.isArray(parsed.result) ? parsed.result : [],
        }
    } catch (error) {
        return {
            ...fromError(error, 'Failed to load comments'),
            result: [],
        }
    }
}

export const addPrototypeComment = async (
    id,
    { body, shape, screen, versionId } = {}
) => {
    try {
        const data = {
            body,
            shape,
            ...(versionId != null ? { versionId } : {}),
            ...(screen != null
                ? {
                      screen: {
                          ...screen,
                          trigger: screen.trigger ?? null,
                          chain: Array.isArray(screen.chain)
                              ? screen.chain
                              : [],
                          // Frozen DOM of the screen. Omitted when absent
                          // rather than sent null.
                          ...(typeof screen.snapshot === 'string' &&
                          screen.snapshot
                              ? { snapshot: screen.snapshot }
                              : {}),
                          from: screen.from ?? null,
                      },
                  }
                : {}),
        }
        const response = await axiosNew({
            url: `${BASE}/${id}/comment`,
            method: 'POST',
            data,
        })

        return fromResponse(response, 'Failed to add comment')
    } catch (error) {
        return fromError(error, 'Failed to add comment')
    }
}

export const updatePrototypeComment = async (
    commentId,
    { body, shape, screen } = {}
) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/comment/${commentId}`,
            method: 'PUT',
            data: {
                ...(body != null ? { body } : {}),
                ...(shape != null ? { shape } : {}),
                ...(screen != null
                    ? {
                          screen: {
                              ...screen,
                              trigger: screen.trigger ?? null,
                              chain: Array.isArray(screen.chain)
                                  ? screen.chain
                                  : [],
                              // Frozen DOM of the screen. Omitted when absent
                              // rather than sent null, so an update never wipes
                              // a snapshot the create stored.
                              ...(typeof screen.snapshot === 'string' &&
                              screen.snapshot
                                  ? { snapshot: screen.snapshot }
                                  : {}),
                              from: screen.from ?? null,
                          },
                      }
                    : {}),
            },
        })
        return fromResponse(response, 'Failed to update comment')
    } catch (error) {
        return fromError(error, 'Failed to update comment')
    }
}

export const deletePrototypeComment = async (commentId) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/comment/${commentId}`,
            method: 'DELETE',
        })
        return fromResponse(response, 'Failed to delete comment')
    } catch (error) {
        return fromError(error, 'Failed to delete comment')
    }
}

export const resolvePrototypeComment = async (commentId, isResolved) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/comment/${commentId}/resolve`,
            method: 'PUT',
            data: { isResolved: Boolean(isResolved) },
        })
        return fromResponse(response, 'Failed to update comment')
    } catch (error) {
        return fromError(error, 'Failed to update comment')
    }
}

export const addPrototypeReply = async (commentId, { body } = {}) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/comment/${commentId}/reply`,
            method: 'POST',
            data: { body },
        })
        return fromResponse(response, 'Failed to add reply')
    } catch (error) {
        return fromError(error, 'Failed to add reply')
    }
}

export const deletePrototypeReply = async (commentId, replyId) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/comment/${commentId}/reply/${replyId}`,
            method: 'DELETE',
        })
        return fromResponse(response, 'Failed to delete reply')
    } catch (error) {
        return fromError(error, 'Failed to delete reply')
    }
}

/** Download prototype comments export (xlsx/csv blob from server). */
export const exportPrototypeComments = async (
    prototypeId,
    { versionId, fileName } = {}
) => {
    try {
        if (!prototypeId) {
            return {
                success: false,
                message: 'Prototype id is required',
            }
        }

        const response = await axiosNew({
            url: `${BASE}/${prototypeId}/comments/export`,
            method: 'POST',
            data: {
                ...(versionId ? { versionId } : {}),
            },
            responseType: 'blob',
        })

        const contentType = response?.headers?.['content-type'] || ''
        if (contentType.includes('application/json')) {
            const text = await response.data.text()
            let message = 'Failed to export comments'
            try {
                const json = JSON.parse(text)
                message = pickMessage(json, message)
            } catch {
                // keep fallback message
            }
            return { success: false, message }
        }

        let downloadName =
            String(fileName || '').trim() ||
            `Prototype_Comments_${prototypeId}.xlsx`
        const disposition = response?.headers?.['content-disposition'] || ''
        const dispositionMatch = disposition.match(
            /filename\*?=(?:UTF-8''|["']?)([^;"'\n]+)/i
        )
        if (dispositionMatch?.[1]) {
            downloadName = decodeURIComponent(
                dispositionMatch[1].replace(/['"]/g, '')
            )
        }

        const blob = new Blob([response.data], {
            type:
                contentType ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = downloadName
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        return { success: true, message: 'Comments exported successfully' }
    } catch (error) {
        let message = error?.message || 'Failed to export comments'

        const errorData = error?.response?.data
        if (errorData instanceof Blob) {
            try {
                const text = await errorData.text()
                const json = JSON.parse(text)
                message = pickMessage(json, message)
            } catch {
                // keep fallback message
            }
        } else if (errorData?.message) {
            message = pickMessage(errorData, message)
        }

        return {
            success: false,
            message,
        }
    }
}

export const listPrototypeActivity = async (
    id,
    { versionId, skip = 0, limit = 50 } = {}
) => {
    try {
        const response = await axiosNew({
            url: `${BASE}/${id}/activity`,
            method: 'POST',
            data: {
                ...(versionId ? { versionId } : {}),
                skip,
                limit,
            },
        })
        const parsed = fromResponse(response, 'Failed to load activity')
        return {
            ...parsed,
            result: Array.isArray(parsed.result) ? parsed.result : [],
        }
    } catch (error) {
        return {
            ...fromError(error, 'Failed to load activity'),
            result: [],
        }
    }
}
